import React from "react";
import {
  LayoutDashboard,
  Map,
  Bike,
  Building2,
  Users,
  BarChart3,
  Settings,
  ChevronLeft,
  Navigation,
  Clock,
  Upload,
  MapPin,
  Tags,
  TrendingUp,
} from "lucide-react";
import { useAppStore } from "../../store/useAppStore";
import { useAuth } from "../../hooks/useAuth";
import { useAppNavigate } from "../../hooks/useAppNavigate";
import { isFleetScopedRole } from "../../utils/authRoles";
import { cn } from "../../utils/helpers";

const adminOnlyNavigation = [
  { name: "Fleets", icon: Building2, id: "fleets" as const },
  { name: "Users", icon: Users, id: "users" as const },
];

const adminNavigation = [
  { name: "Dashboard", icon: LayoutDashboard, id: "dashboard" as const },
  { name: "Live Map", icon: Map, id: "map" as const },
  { name: "History", icon: Clock, id: "history" as const },
  { name: "Import Data", icon: Upload, id: "import" as const },
  { name: "Vehicles", icon: Bike, id: "vehicles" as const },
  ...adminOnlyNavigation,
  { name: "GPS Data", icon: MapPin, id: "gps-data" as const },
  { name: "Campaigns", icon: Tags, id: "campaigns" as const },
  { name: "Impression Jobs", icon: TrendingUp, id: "impression-jobs" as const },
  { name: "Reports", icon: BarChart3, id: "reports" as const },
];

const fleetNavigation = [{ name: "Live Map", icon: Map, id: "map" as const }];

export const Sidebar: React.FC = () => {
  const { sidebarOpen, setSidebarOpen, currentView } = useAppStore();
  const { user } = useAuth();
  const navigateToView = useAppNavigate();
  const isAdmin = user?.role === "admin";
  const navigation = isFleetScopedRole(user?.role)
    ? fleetNavigation
    : isAdmin
      ? adminNavigation
      : adminNavigation.filter(
          (item) => !adminOnlyNavigation.some((a) => a.id === item.id),
        );
  const showSettings = isAdmin;

  if (!sidebarOpen) {
    return (
      <div className="w-16 bg-gray-900 flex flex-col">
        <div className="flex items-center justify-center h-16 border-b border-gray-700">
          <Navigation className="w-8 h-8 text-primary-400" />
        </div>

        <nav className="flex-1 pt-4">
          <div className="space-y-2 px-2">
            {navigation.map((item) => (
              <button
                key={item.id}
                onClick={() => navigateToView(item.id)}
                className={cn(
                  "w-full p-3 rounded-md text-left transition-colors",
                  currentView === item.id
                    ? "bg-primary-600 text-white"
                    : "text-gray-300 hover:bg-gray-700 hover:text-white",
                )}
                title={item.name}
              >
                <item.icon className="w-5 h-5 mx-auto" />
              </button>
            ))}
          </div>
        </nav>

        <div className="p-2">
          <button
            onClick={() => setSidebarOpen(true)}
            className="w-full p-3 rounded-md text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
            title="Expand sidebar"
          >
            <ChevronLeft className="w-5 h-5 mx-auto rotate-180" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-64 bg-gray-900 flex flex-col">
      <div className="flex items-center justify-between h-16 px-4 border-b border-gray-700">
        <div className="flex items-center space-x-2">
          <Navigation className="w-8 h-8 text-primary-400" />
          <span className="text-white font-semibold">Vehicle Tracker</span>
        </div>
        <button
          onClick={() => setSidebarOpen(false)}
          className="p-1 rounded-md text-gray-400 hover:text-white transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
      </div>

      <nav className="flex-1 pt-4">
        <div className="space-y-2 px-3">
          {navigation.map((item) => (
            <button
              key={item.id}
              onClick={() => navigateToView(item.id)}
              className={cn(
                "w-full flex items-center space-x-3 px-3 py-2 rounded-md text-left transition-colors",
                currentView === item.id
                  ? "bg-primary-600 text-white"
                  : "text-gray-300 hover:bg-gray-700 hover:text-white",
              )}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.name}</span>
            </button>
          ))}
        </div>
      </nav>

      {showSettings && (
        <div className="p-3 border-t border-gray-700">
          <button
            onClick={() => navigateToView("settings")}
            className={cn(
              "w-full flex items-center space-x-3 px-3 py-2 rounded-md transition-colors",
              currentView === "settings"
                ? "bg-primary-600 text-white"
                : "text-gray-300 hover:bg-gray-700 hover:text-white",
            )}
          >
            <Settings className="w-5 h-5" />
            <span>Settings</span>
          </button>
        </div>
      )}
    </div>
  );
};
